import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'

/**
 * Where the pictures are on the round trip the switch takes them on.
 *
 * FOUR STATES, NOT A BOOLEAN, AND EACH ONE EARNS ITS PLACE:
 *
 * - `shown` — as published. What the prerendered document carries and what the
 *   first client render must agree with.
 * - `leaving` — still in the DOM, collapsing. The stylesheet has this frame to
 *   fade the figures out and give their width back to the verse.
 * - `hidden` — gone. Nothing of the artwork is in the document.
 * - `restored` — back, and brought back by the reader rather than published that
 *   way. Identical to `shown` in every respect but one: `_poems.sass` hangs its
 *   `@starting-style` off this value, so a figure entering the DOM grows in
 *   instead of appearing whole.
 *
 * THAT LAST DISTINCTION IS THE WHOLE REASON `restored` IS NOT JUST `shown`.
 * `@starting-style` fires for an element's first style resolution — including the
 * elements the prerendered HTML already contains, on first paint. Scoped to
 * `shown` it would animate all eight figures in on every page load: motion nobody
 * asked for, and a layout shift with no `hadRecentInput` flag to excuse it, which
 * is the CLS gate. Scoped to `restored` it can only ever run after a press.
 */
export type IllustrationsPhase = 'shown' | 'leaving' | 'hidden' | 'restored'

export interface IllustrationsState {
  /** For `data-illustrations` on the page root, which is what the fade keys off. */
  readonly phase: IllustrationsPhase
  /** What the reader asked for, and what `aria-pressed` reports. */
  readonly visible: boolean
  /** Whether the artwork is in the DOM. Stays true across the whole fade out. */
  readonly mounted: boolean
  readonly toggle: () => void
}

/**
 * What the server renders and what the client renders on its very first pass.
 *
 * A CONSTANT RATHER THAN A PARAMETER, and that is the point: the prerendered
 * document is the one a crawler reads and the one that paints before any script
 * runs, and the pictures belong in it. Making the initial state configurable would
 * make "the prerendered poem is illustrated" a caller's choice rather than a fact.
 */
const INITIAL_PHASE: IllustrationsPhase = 'shown'

/**
 * How long the figures stay mounted after the reader turns them off.
 *
 * THIS NUMBER IS `--motion-duration`, and it is duplicated here because there is
 * nowhere else for it to live: unmounting is a React decision and the fade is a
 * CSS one, and the only way to read the token from TypeScript is a
 * `getComputedStyle` call — a forced style read on the interaction path, to learn
 * a constant. `use-illustrations.test.ts` parses `_tokens.sass` and fails if the
 * two ever drift, which is the same trick that holds `robots.txt` to
 * `SITE_CONFIG.origin`.
 *
 * UNDER `prefers-reduced-motion` THE TOKEN GOES TO 0ms AND THIS DOES NOT, and
 * that is correct rather than an oversight. The fade is already over — the reader
 * sees the pictures go at once — and the nodes then sit invisible for a fifth of a
 * second before React takes them out. Nothing is on screen to wait for, so there
 * is nothing to feel.
 */
export const FADE_MS = 200

/**
 * The machine, as a table rather than as branches.
 *
 * A press during the fade out is the interesting entry: `leaving` goes straight
 * back to `restored`, so the figures reverse from wherever they had collapsed to
 * instead of finishing the exit and starting a fresh entrance. The effect below
 * cancels the pending unmount on the same render.
 */
const NEXT_PHASE: Readonly<Record<IllustrationsPhase, IllustrationsPhase>> = {
  shown: 'leaving',
  restored: 'leaving',
  leaving: 'restored',
  hidden: 'restored',
}

/**
 * Whether the poem route draws its pictures, and how they come and go.
 *
 * THE FIGURES ARE UNMOUNTED, NOT HIDDEN — see `poem-article.tsx`. This hook owns
 * only the phase; what it means is the article's business and the stylesheet's.
 *
 * WHAT CHANGED, AND WHY IT IS HERE RATHER THAN IN CSS. The switch used to flip a
 * boolean, so a poem's worth of artwork appeared and vanished between one frame
 * and the next, taking the verse's width with it. CSS alone cannot soften that:
 * a transition needs the element to still exist while it runs, and an element
 * React has unmounted does not. So the exit is held open for exactly one fade —
 * `leaving` — and the unmount happens on the far side of it.
 *
 * THE ENTRANCE NEEDS NO TIMER AT ALL, which is why there is only one. A figure
 * coming back is a newly mounted element, and `@starting-style` in `_poems.sass`
 * gives it a before-change style to grow from. The alternative — mounting it
 * collapsed and flipping a class two animation frames later — would put a pair of
 * `requestAnimationFrame` callbacks on the interaction path to do what the
 * cascade already does for free.
 *
 * NOTHING IS PERSISTED, exactly as in `use-reading-scale.ts` and for the same two
 * reasons: the app stays stateless end to end, and the first client render matches
 * the prerendered HTML so hydration cannot mismatch and CLS stays at 0.0.
 *
 * The cost is real and worth naming: every link on this site is a full document
 * load, so a reader who hides the pictures gets them back on the next poem. Making
 * the choice survive that would take a second pre-paint bootstrap script beside
 * `theme-bootstrap.ts` — the alternative, reading storage after mount, would paint
 * the figures and then yank them out from under the verse on every load.
 *
 * The unmount is wrapped in `startTransition` so dropping a poem's worth of
 * figures can never block an in-flight interaction (INP budget,
 * rules/architecture.md). The press itself no longer needs it: all it does now is
 * move one attribute and let the cascade run.
 */
export function useIllustrations(): IllustrationsState {
  const [phase, setPhase] = useState<IllustrationsPhase>(INITIAL_PHASE)

  /**
   * The pending unmount, owned by the phase that scheduled it.
   *
   * Keyed on `phase` rather than held in a ref, so every way out of `leaving`
   * cancels the timer through the same cleanup: a second press, and teardown of
   * the route itself. An uncancelled timer calling `setState` after unmount is a
   * React warning, and the Lighthouse gate is `errors-in-console: maxLength 0`.
   */
  useEffect(() => {
    if (phase !== 'leaving') return undefined

    const timer = setTimeout(() => {
      startTransition(() => {
        setPhase('hidden')
      })
    }, FADE_MS)

    return () => {
      clearTimeout(timer)
    }
  }, [phase])

  const toggle = useCallback(() => {
    setPhase((current) => NEXT_PHASE[current])
  }, [])

  const visible = phase === 'shown' || phase === 'restored'

  return useMemo(
    () => ({ phase, visible, mounted: phase !== 'hidden', toggle }),
    [phase, visible, toggle],
  )
}
